import { useEffect, useRef } from 'react';

export default function ReconstructionViz({ variant = 'dark' }) {
  const ref = useRef();

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    let W = 0;
    let H = 0;
    let dpr = 1;
    let raf;
    let active = true;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const points = [];
    const isLight = variant === 'light';
    const neutral = isLight ? '32,36,28' : '225,229,216';
    const rand = (i) => ((Math.sin(i * 9283.31) + 1) * 43758.5453) % 1;

    for (let i = 0; i < 1400; i += 1) {
      const wall = i % 5;
      const a = rand(i) * 2 - 1;
      const b = rand(i + 31) * 2 - 1;
      const p = wall === 0 ? [a, b, -1]
        : wall === 1 ? [a, 1, b]
        : wall === 2 ? [-1, b, a]
        : wall === 3 ? [a, -1, b]
        : [a * 0.72, 1, b * 0.72];
      points.push({ p, delay: rand(i + 72), bright: rand(i + 9) > 0.9 });
    }
    for (let i = 0; i < 220; i += 1) {
      const angle = (i / 130) * Math.PI * 2;
      points.push({ p: [0.4 + Math.cos(angle) * 0.28, 0.5 - rand(i) * 0.95, Math.sin(angle) * 0.28], delay: rand(i + 4), bright: i % 19 === 0 });
    }

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = canvas.offsetWidth;
      H = canvas.offsetHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function project([x, y, z], rot) {
      const c = Math.cos(rot);
      const s = Math.sin(rot);
      const rx = x * c - z * s;
      const rz = x * s + z * c;
      const scale = Math.min(W, H) * 0.27 * (2.8 / (3.2 + rz));
      return { x: W * 0.59 + rx * scale, y: H * 0.51 + y * scale - rz * scale * 0.17, z: rz, scale };
    }

    function line(a, b, rot, alpha = 0.18, dash = []) {
      const p1 = project(a, rot);
      const p2 = project(b, rot);
      ctx.beginPath();
      ctx.setLineDash(dash);
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.strokeStyle = `rgba(${isLight ? '46,122,83' : '200,240,74'},${alpha})`;
      ctx.lineWidth = 0.8;
      ctx.stroke();
      ctx.setLineDash([]);
    }

    function frame(now) {
      const time = now / 1000;
      const cycle = (time % 10) / 10;
      const reveal = Math.min(1, cycle * 2.2);
      const fade = cycle > 0.9 ? (1 - cycle) * 10 : 1;
      const rot = -0.5 + Math.sin(time * 0.13) * 0.17;
      ctx.clearRect(0, 0, W, H);
      if (isLight) {
        ctx.fillStyle = '#dfe6dc';
        ctx.fillRect(0, 0, W, H);
      }

      const grid = ctx.createLinearGradient(0, 0, W, H);
      grid.addColorStop(0, isLight ? 'rgba(36,111,75,0.07)' : 'rgba(200,240,74,0.025)');
      grid.addColorStop(1, isLight ? 'rgba(20,27,23,0.025)' : 'rgba(240,239,232,0.01)');
      ctx.strokeStyle = grid;
      ctx.lineWidth = 0.5;
      for (let x = (time * 7) % 64; x < W; x += 64) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
      for (let y = 0; y < H; y += 64) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

      points
        .map((point) => ({ ...point, screen: project(point.p, rot) }))
        .sort((a, b) => a.screen.z - b.screen.z)
        .forEach(({ delay, bright, screen }) => {
          if (delay > reveal) return;
          const local = Math.min(1, (reveal - delay) * 8) * fade;
          ctx.beginPath();
          ctx.arc(screen.x, screen.y, bright ? (isLight ? 2.1 : 1.45) : (isLight ? 1.05 : 0.65), 0, Math.PI * 2);
          ctx.fillStyle = bright
            ? `rgba(${isLight ? '139,176,0' : '200,240,74'},${0.86 * local})`
            : `rgba(${neutral},${(isLight ? 0.34 : 0.16) + (screen.z + 1) * 0.1 * local})`;
          ctx.fill();
        });

      const corners = [[-1,-1,-1],[1,-1,-1],[1,1,-1],[-1,1,-1],[-1,-1,1],[1,-1,1],[1,1,1],[-1,1,1]];
      [[0,1],[1,2],[2,3],[3,0],[0,4],[3,7],[4,5],[5,6],[6,7]].forEach(([a,b]) => line(corners[a], corners[b], rot, 0.13 * reveal * fade));
      line([-1, 1.12, 1], [1, 1.12, 1], rot, 0.55 * fade, [3, 4]);

      const origin = project([0, 0.15, 0.05], rot);
      for (let r = 0; r < 5; r += 1) {
        const ang = -0.35 + r * 0.28 + Math.sin(time * 0.4) * 0.04;
        ctx.beginPath();
        ctx.moveTo(origin.x, origin.y);
        ctx.lineTo(origin.x + Math.cos(ang) * W * 0.42, origin.y + Math.sin(ang) * H * 0.18);
        ctx.strokeStyle = `rgba(200,240,74,${0.16 * fade})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      const scanY = H * (0.16 + ((time * 0.08) % 1) * 0.7);
      const scan = ctx.createLinearGradient(W * 0.22, 0, W * 0.9, 0);
      scan.addColorStop(0, 'transparent');
      scan.addColorStop(0.5, isLight ? 'rgba(26,143,101,0.42)' : 'rgba(200,240,74,0.35)');
      scan.addColorStop(1, 'transparent');
      ctx.fillStyle = scan;
      ctx.fillRect(W * 0.18, scanY, W * 0.76, 1);

      if (active && !reduceMotion) raf = requestAnimationFrame(frame);
    }

    resize();
    if (reduceMotion) frame(7000);
    else raf = requestAnimationFrame(frame);
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    const visibility = new IntersectionObserver(([entry]) => {
      const next = entry.isIntersecting;
      if (next === active) return;
      active = next;
      cancelAnimationFrame(raf);
      if (active) {
        if (reduceMotion) frame(7000);
        else raf = requestAnimationFrame(frame);
      }
    }, { rootMargin: '120px' });
    visibility.observe(canvas);
    return () => {
      active = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
      visibility.disconnect();
    };
  }, [variant]);

  return (
    <canvas
      ref={ref}
      className="bv-viz"
      aria-label="Animated reconstruction visualization"
    />
  );
}
